import type { actions, paths } from '@luxuryescapes/contract-svc-merchandise';

import { json_headers, request } from './common';

const BASE_PATH = `${window.configs.API_HOST}/api/merchandise`;

export type MerchandiseOrderGetResponse =
  paths['/api/merchandise/orders/{id}']['get']['responses']['200']['content']['application/json'];

export type MerchandiseItemType = MerchandiseOrderGetResponse['result']['items'][number];

export function getMerchandiseOrder(orderId: string): Promise<MerchandiseOrderGetResponse> {
  const uri = `${BASE_PATH}/orders/${orderId}`;

  return request(uri, { method: 'GET' });
}

export type MerchandiseAdvertGetResponse = actions['getAdvert']['responses']['200']['content']['application/json'];
export type MerchandiseAdvertType = MerchandiseAdvertGetResponse['result']['advert'];

export function getMerchandiseAdvert(advertId: string): Promise<MerchandiseAdvertType> {
  const uri = `${BASE_PATH}/adverts/${advertId}`;

  return request(uri, { method: 'GET' }).then((response) => response.result.advert);
}

export type AdvertsGetResponse =
  paths['/api/merchandise/adverts']['get']['responses']['200']['content']['application/json'];

export function getMerchandiseAdverts(
  advertIds: string[],
  options?: { signal?: AbortSignal },
): Promise<AdvertsGetResponse> {
  const idsParam = advertIds.join(',');
  const uri = `${BASE_PATH}/adverts?advertIds=${encodeURIComponent(idsParam)}`;

  return request(uri, { method: 'GET', signal: options?.signal });
}

// Admin endpoint for enriched advert data (includes isOnline, isDeleted, salesCount, etc.)
export function getAdminAdverts(advertIds: string[], options?: { signal?: AbortSignal }): Promise<AdvertsGetResponse> {
  const idsParam = advertIds.join(',');
  const uri = `${BASE_PATH}/admin/adverts?advertIds=${encodeURIComponent(idsParam)}`;

  return request(uri, { method: 'GET', signal: options?.signal });
}

export type ColorsGetResponse =
  paths['/api/merchandise/colors']['get']['responses']['200']['content']['application/json'];

export type Color = ColorsGetResponse['result']['colors'][number];

export type CreateColorRequest = paths['/api/merchandise/colors']['post']['requestBody']['content']['application/json'];

export type CreateColorResponse =
  paths['/api/merchandise/colors']['post']['responses']['201']['content']['application/json'];

export type UpdateColorRequest =
  paths['/api/merchandise/colors/{name}']['put']['requestBody']['content']['application/json'];

export type UpdateColorResponse =
  paths['/api/merchandise/colors/{name}']['put']['responses']['200']['content']['application/json'];

export function getColors(options?: { signal?: AbortSignal }): Promise<ColorsGetResponse> {
  const uri = `${BASE_PATH}/colors`;

  return request(uri, { method: 'GET', signal: options?.signal });
}

export function updateColor(colorName: string, colorData: UpdateColorRequest): Promise<UpdateColorResponse> {
  return request(`${BASE_PATH}/colors/${colorName}`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify(colorData),
  });
}

export function createColor(colorData: CreateColorRequest): Promise<CreateColorResponse> {
  return request(`${BASE_PATH}/colors`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(colorData),
  });
}

export function deleteColor(colorName: string): Promise<void> {
  return request(`${BASE_PATH}/colors/${colorName}`, {
    method: 'DELETE',
  });
}

// Entity Rankings types
export interface EntityRanking {
  id: string;
  entityType: string;
  entityKey: string; // advertId for ADVERT type
  rank: number;
  createdAt: string;
  updatedAt: string;
}

export interface EntityRankingsGetResponse {
  status: number;
  message: string;
  result: {
    entityRankings: EntityRanking[];
  };
}

export function getEntityRankings(
  entityType: string,
  options?: { signal?: AbortSignal },
): Promise<EntityRankingsGetResponse> {
  const uri = `${BASE_PATH}/entity-rankings?entityType=${encodeURIComponent(entityType)}`;

  return request(uri, { method: 'GET', signal: options?.signal });
}

// Combined display type for ranking table (merged from rankings + adverts)
export interface AdvertRanking {
  rank: number;
  advertId: string;
  brandName: string | null;
  categoryName: string | null;
  title: string | null;
  slug: string | null;
  isOnline: boolean | null;
  salesCount: number | null;
  createdAt: string | null;
  updatedAt: string | null;
  isDeleted: boolean | null;
}

export type SearchAdvertsResponse =
  paths['/api/merchandise/search/adverts']['get']['responses']['200']['content']['application/json'];

export type AdvertSearchResult = SearchAdvertsResponse['result']['adverts'][number];

export function searchAdverts(query: string, options?: { signal?: AbortSignal }): Promise<SearchAdvertsResponse> {
  const uri = `${BASE_PATH}/search/adverts?name=${encodeURIComponent(query)}&sortBy=recommended`;

  return request(uri, { method: 'GET', signal: options?.signal });
}

export interface UpdateEntityRankingsResponse {
  status: number;
  message: string;
}

export function updateEntityRankings(entityKeys: string[]): Promise<UpdateEntityRankingsResponse> {
  return request(`${BASE_PATH}/entity-rankings?entityType=ADVERT`, {
    method: 'PUT',
    headers: json_headers,
    body: JSON.stringify({ entityKeys }),
  });
}
